import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, Music, ShoppingBag, Heart, ExternalLink, Headphones } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHABA_AVATAR = "https://customer-assets.emergentagent.com/job_jonesaica-portal/artifacts/h7rpnpq7_IMG_4438.jpg";
const SHABA_INTRO_VIDEO = "https://customer-assets.emergentagent.com/job_8ed5ec52-990e-4af6-908a-8b1627b4f2b5/artifacts/7owgx5f1_shaba%20shara%20intro.mp4";

const SHABA_YOUTUBE = "https://youtube.com/@shabasharah?si=94NEL8uvqAqjsCg4";
const SHABA_TIKTOK = "https://www.tiktok.com/@shabasharah";
const SHABA_CASHAPP = "https://cash.app/$jonesaicayahsharal";
const SHABA_DISPLAY_NAME = "Shaba Sharah";

function ShabaMusicPlayer({ song }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const { addItem } = useCart();

  const previewUrl = song.preview_file_url || song.audio_file_url;
  const maxDuration = song.preview_duration_seconds || 20;

  const togglePlay = () => {
    if (!previewUrl) { toast.error("No preview available"); return; }
    if (playing) {
      audioRef.current?.pause();
      clearInterval(timerRef.current);
      setPlaying(false);
    } else {
      const audio = audioRef.current;
      audio.currentTime = 0;
      audio.play().catch(() => toast.error("Unable to play audio"));
      setPlaying(true);
      setProgress(0);
      timerRef.current = setInterval(() => {
        if (audio.currentTime >= maxDuration) {
          audio.pause();
          clearInterval(timerRef.current);
          setPlaying(false);
          setProgress(0);
        } else {
          setProgress((audio.currentTime / maxDuration) * 100);
        }
      }, 100);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    return () => { clearInterval(timerRef.current); audio?.pause(); };
  }, []);

  return (
    <Card className="bg-slate-900 border-slate-800 hover:border-kingdom-gold/60 transition-colors" data-testid={`shaba-song-${song.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={togglePlay}
            className="w-12 h-12 rounded-full bg-royal-purple text-white flex items-center justify-center hover:bg-royal-purple/80 transition flex-shrink-0"
            data-testid={`shaba-play-${song.id}`}
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white truncate">{song.title}</h3>
            <p className="text-xs text-purple-300">{SHABA_DISPLAY_NAME}</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-kingdom-gold rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-slate-400">{maxDuration}s preview</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="font-bold text-kingdom-gold">${song.price?.toFixed(2)}</span>
            <div className="mt-1">
              <button
                type="button"
                className="btn-gold text-xs px-3 py-1 rounded font-semibold"
                onClick={() => addItem({
                  internal_id: song.id,
                  name: song.title,
                  price: Number(song.price || 0.99),
                  type: "one_time",
                  image: song.cover_image_url || null,
                })}
                data-testid={`shaba-buy-${song.id}`}
              >
                Add ${(song.price || 0.99).toFixed(2)}
              </button>
            </div>
          </div>
        </div>
        <audio ref={audioRef} src={previewUrl} preload="none" />
      </CardContent>
    </Card>
  );
}

export default function Shaba() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchMusic = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        const musicProducts = (res.data || []).filter(p => p.product_type === "music");
        setSongs(musicProducts);
      } catch {
        toast.error("Failed to load music");
      } finally {
        setLoading(false);
      }
    };
    fetchMusic();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="shaba-page">
      {/* Hero — dark dramatic for strong text contrast */}
      <section className="relative py-20 overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-royal-purple/60 via-black to-slate-950" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-kingdom-gold text-sm font-bold uppercase tracking-wider">
                Artist Hub
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
                Shaba Sharah
              </h1>
              <p className="text-xl text-purple-200 mb-2">Naby'ah Shaba · Sharah Tzadaq'ah YY</p>
              <p className="text-slate-400 text-lg mb-8">
                Scripture-based music proclaiming the Name of YaHUaH/YaHUSHA.
                Reggae, dancehall, and prophetic fire.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/shop">
                  <Button className="bg-kingdom-gold hover:bg-kingdom-gold/90 text-black font-bold" data-testid="shaba-shop-music-btn">
                    <Music className="w-4 h-4 mr-2" />
                    Shop Music
                  </Button>
                </a>
                <a href="https://www.etsy.com/shop/AncientPathFactory" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-purple-400 text-purple-400 hover:bg-purple-400/10" data-testid="shaba-shop-merch-btn">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Shop Merch
                  </Button>
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative max-w-sm w-full rounded-lg overflow-hidden shadow-2xl shadow-purple-900/50 border-2 border-kingdom-gold/40" data-testid="shaba-hero-media">
                <video
                  src={SHABA_INTRO_VIDEO}
                  poster={SHABA_AVATAR}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-auto block bg-slate-900"
                  data-testid="shaba-intro-video"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/80 to-transparent p-3">
                  <p className="text-xs text-kingdom-gold uppercase tracking-wider font-semibold">Shaba Sharah — Intro</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Music Grid - DB-backed, $0.99 singles, 20s preview only */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">Discography</span>
            <h2 className="font-heading text-3xl text-white mt-2">Releases</h2>
            <p className="text-slate-400 mt-2">
              Preview 20 seconds of each track. Purchase for full download.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8"><p className="text-slate-500">Loading music...</p></div>
          ) : songs.length === 0 ? (
            <div className="text-center py-8"><p className="text-slate-500">No music available yet.</p></div>
          ) : (
            <div className="space-y-4" data-testid="shaba-song-list">
              {songs.map(song => <ShabaMusicPlayer key={song.id} song={song} />)}
            </div>
          )}

          <div className="text-center mt-8">
            <a href="/shop">
              <Button className="btn-gold" data-testid="shaba-view-shop-btn">
                <ShoppingBag className="w-4 h-4 mr-2" />View Full Shop
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 bg-royal-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Heart className="w-12 h-12 text-kingdom-gold mx-auto mb-4" />
            <h2 className="font-heading text-3xl text-white mb-4">Support the Artist</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Become a monthly supporter and receive exclusive benefits.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Supporter */}
            <div className="bg-slate-950 border border-slate-800 p-6 rounded-lg text-center" data-testid="shaba-tier-supporter-monthly">
              <h3 className="text-white text-xl font-bold">Supporter</h3>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$10<span className="text-sm text-slate-400">/mo</span></p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6">
                <li>&bull; 1 free song download/month</li>
                <li>&bull; Early access to new releases</li>
              </ul>
              <button type="button" className="block w-full bg-purple-700 hover:bg-purple-600 text-white py-2 rounded font-semibold" onClick={() => addItem({ internal_id: "supporter_monthly", name: "Supporter (Monthly)", price: 10, type: "subscription" })} data-testid="shaba-tier-subscribe-supporter-monthly">Subscribe Monthly</button>
            </div>
            {/* Builder */}
            <div className="bg-slate-950 border border-slate-800 p-6 rounded-lg text-center" data-testid="shaba-tier-builder-monthly">
              <h3 className="text-white text-xl font-bold">Builder</h3>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$25<span className="text-sm text-slate-400">/mo</span></p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6">
                <li>&bull; 1 free song download/month</li>
                <li>&bull; Exclusive content</li>
                <li>&bull; Monthly calls</li>
              </ul>
              <button type="button" className="block w-full bg-purple-700 hover:bg-purple-600 text-white py-2 rounded font-semibold" onClick={() => addItem({ internal_id: "builder_monthly", name: "Builder (Monthly)", price: 25, type: "subscription" })} data-testid="shaba-tier-subscribe-builder-monthly">Subscribe Monthly</button>
            </div>
            {/* Patron */}
            <div className="bg-slate-950 border border-slate-800 p-6 rounded-lg text-center" data-testid="shaba-tier-patron-monthly">
              <h3 className="text-white text-xl font-bold">Patron</h3>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$50<span className="text-sm text-slate-400">/mo</span></p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6">
                <li>&bull; All benefits above</li>
                <li>&bull; One-time merch item</li>
                <li>&bull; Priority support</li>
              </ul>
              <button type="button" className="block w-full bg-purple-700 hover:bg-purple-600 text-white py-2 rounded font-semibold" onClick={() => addItem({ internal_id: "patron_monthly", name: "Patron (Monthly)", price: 50, type: "subscription" })} data-testid="shaba-tier-subscribe-patron-monthly">Subscribe Monthly</button>
            </div>
          </div>

          {/* Direct Cash App tip */}
          <div className="mt-10 max-w-2xl mx-auto bg-gradient-to-r from-kingdom-gold/20 to-purple-700/20 border border-kingdom-gold/40 rounded-lg p-6 text-center" data-testid="shaba-cashapp-block">
            <p className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider mb-2">Tip the Artist Directly</p>
            <p className="text-slate-300 text-sm mb-4">Send one-time support straight to Shaba Sharah on Cash App.</p>
            <a href={SHABA_CASHAPP} target="_blank" rel="noopener noreferrer" data-testid="shaba-cashapp-link">
              <Button className="btn-gold">$jonesaicayahsharal on Cash App</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Bio */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">About</span>
            <h2 className="font-heading text-3xl text-white mt-2">The Artist</h2>
          </div>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-8">
              <p className="text-slate-300 leading-relaxed">
                Shaba Sharah is a prophetic reggae and dancehall artist from The Kingdom of Yunah'Shach'Qal Yashar'al.
                Her music draws from scripture, Torah observance, and the rich rhythms of Caribbean sound system culture.
                Each track is a declaration of amunah and identity, blending spiritual warfare with island flare.
              </p>
              <p className="text-slate-300 leading-relaxed mt-4">
                The Babylon Contract Mash Up collection features songs of freedom, covenant, and deliverance —
                from Pasach Skank to Blood Pan Di Doorpost, each track carries prophetic weight and musical fire.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Social Links */}
      <section className="bg-gradient-to-t from-royal-purple/30 to-slate-950 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl text-white mb-6">Follow the Movement</h2>
          <div className="flex justify-center gap-6">
            <a href={SHABA_YOUTUBE} target="_blank" rel="noopener noreferrer" className="p-4 bg-slate-800 rounded-lg hover:bg-red-900/50 transition-colors" data-testid="shaba-youtube-link">
              <svg viewBox="0 0 24 24" className="w-8 h-8 fill-white">
                <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14c-1.88-.5-9.38-.5-9.38-.5s-7.5 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.55 15.57V8.43L15.8 12l-6.25 3.57z" />
              </svg>
            </a>
            <a href={SHABA_TIKTOK} target="_blank" rel="noopener noreferrer" className="p-4 bg-slate-800 rounded-lg hover:bg-purple-900/50 transition-colors" data-testid="shaba-tiktok-link">
              <svg viewBox="0 0 24 24" className="w-8 h-8 fill-white">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
            </a>
            <a href="https://www.etsy.com/shop/AncientPathFactory" target="_blank" rel="noopener noreferrer" className="p-4 bg-slate-800 rounded-lg hover:bg-kingdom-gold/30 transition-colors" data-testid="shaba-etsy-link">
              <ExternalLink className="w-8 h-8 text-white" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
